import type { Theme } from '@material-ui/core';

import type { FontWeight } from 'vega';
import type { Config } from 'vega-embed';

import {
  getConfidenceCategoricalColors,
  getConfidencePrimaryChartColor,
} from '@spotify/confidence-charts-react';

const resolveFontSize = (
  fontSize: unknown,
  theme: Theme,
  fallback: number,
): number => {
  if (typeof fontSize === 'number') {
    return fontSize;
  }

  if (typeof fontSize !== 'string') {
    return fallback;
  }

  const parsedFontSize = Number.parseFloat(fontSize);
  if (!Number.isFinite(parsedFontSize)) {
    return fallback;
  }

  if (fontSize.endsWith('rem')) {
    const remPerPixel = Number.parseFloat(theme.typography.pxToRem(1));
    return Number.isFinite(remPerPixel) && remPerPixel > 0
      ? parsedFontSize / remPerPixel
      : fallback;
  }

  return parsedFontSize;
};

const encoreColor = (token: string, fallback: string): string =>
  `var(--${token}, ${fallback})`;

const numericFontWeights: FontWeight[] = [
  100, 200, 300, 400, 500, 600, 700, 800, 900,
];

const LEGEND_COLUMN_MIN_WIDTH = 120;

const resolveFontWeight = (fontWeight: unknown): FontWeight | undefined => {
  if (typeof fontWeight === 'number') {
    return numericFontWeights.find(weight => weight === fontWeight);
  }

  if (
    fontWeight === 'normal' ||
    fontWeight === 'bold' ||
    fontWeight === 'lighter' ||
    fontWeight === 'bolder'
  ) {
    return fontWeight;
  }

  return undefined;
};

const resolveFontFamilies = (
  fontFamilies: readonly (string | undefined)[],
): (string | undefined)[] => {
  if (
    !fontFamilies.some(fontFamily => fontFamily?.includes('var(')) ||
    typeof document === 'undefined' ||
    typeof window === 'undefined' ||
    !document.body
  ) {
    return [...fontFamilies];
  }

  const fontProbe = document.createElement('span');
  document.body.appendChild(fontProbe);

  try {
    return fontFamilies.map(fontFamily => {
      if (!fontFamily?.includes('var(')) {
        return fontFamily;
      }

      fontProbe.style.fontFamily = fontFamily;
      return window.getComputedStyle(fontProbe).fontFamily || fontFamily;
    });
  } finally {
    fontProbe.remove();
  }
};

export const buildConfidenceVegaConfig = (theme: Theme): Config => {
  const isDark = theme.palette.type === 'dark';
  const colorMode = isDark ? 'dark' : 'light';
  const categoricalColors = [...getConfidenceCategoricalColors(colorMode)];
  const primaryChartColor = getConfidencePrimaryChartColor(colorMode);
  const captionFontSize = resolveFontSize(
    theme.typography.caption.fontSize,
    theme,
    theme.typography.fontSize,
  );
  const bodyFontSize = resolveFontSize(
    theme.typography.body2.fontSize,
    theme,
    theme.typography.fontSize,
  );
  const unresolvedBodyFontFamily =
    theme.typography.body2.fontFamily ?? theme.typography.fontFamily;
  const unresolvedTitleFontFamily =
    theme.typography.h4.fontFamily ?? theme.typography.fontFamily;
  const [bodyFontFamily, titleFontFamily] = resolveFontFamilies([
    unresolvedBodyFontFamily,
    unresolvedTitleFontFamily,
  ]);
  const markStrokeWidth = theme.spacing(0.25);
  const backgroundElevatedBase = encoreColor(
    'background-elevated-base',
    theme.palette.background.paper,
  );
  const textBase = encoreColor('text-base', theme.palette.text.primary);
  const textSubdued = encoreColor('text-subdued', theme.palette.text.secondary);
  const essentialSubdued = encoreColor(
    'essential-subdued',
    theme.palette.text.disabled,
  );

  return {
    autosize: {
      type: 'fit-x',
      contains: 'padding',
      resize: true,
    },
    background: backgroundElevatedBase,
    padding: theme.spacing(1),
    title: {
      anchor: 'start',
      color: textSubdued,
      subtitleColor: textSubdued,
      font: titleFontFamily,
      subtitleFont: bodyFontFamily,
      fontSize: bodyFontSize,
      subtitleFontSize: bodyFontSize,
      fontWeight: resolveFontWeight(theme.typography.fontWeightMedium),
      subtitleFontWeight: resolveFontWeight(theme.typography.fontWeightRegular),
      offset: theme.spacing(3),
      subtitlePadding: theme.spacing(0.5),
    },
    axis: {
      domain: false,
      ticks: false,
      grid: false,
      gridColor: essentialSubdued,
      gridDash: [theme.spacing(0.5), theme.spacing(0.75)],
      gridOpacity: isDark ? 0.35 : 0.2,
      labelColor: textSubdued,
      labelFont: bodyFontFamily,
      labelFontSize: captionFontSize,
      labelOverlap: 'greedy',
      labelPadding: theme.spacing(0.75),
      labelSeparation: theme.spacing(1),
      labelLimit: theme.spacing(20),
      titleColor: textSubdued,
      titleFont: bodyFontFamily,
      titleFontSize: captionFontSize,
      titleFontWeight: resolveFontWeight(theme.typography.fontWeightMedium),
      titlePadding: theme.spacing(2),
    },
    axisX: {
      domain: true,
      domainColor: essentialSubdued,
      domainWidth: theme.spacing(0.125),
    },
    axisY: {
      domain: false,
    },
    axisDiscrete: {
      grid: false,
      labelAngle: 0,
    },
    axisQuantitative: {
      grid: true,
      tickCount: 6,
    },
    axisTemporal: {
      labelAngle: 0,
    },
    legend: {
      orient: 'bottom',
      direction: 'horizontal',
      columns: {
        signal: `max(1, floor(width / ${LEGEND_COLUMN_MIN_WIDTH}))`,
      },
      labelColor: textSubdued,
      labelFont: bodyFontFamily,
      labelFontSize: bodyFontSize,
      labelLimit: theme.spacing(20),
      titleColor: textBase,
      titleFont: bodyFontFamily,
      titleFontSize: bodyFontSize,
      titleFontWeight: resolveFontWeight(theme.typography.fontWeightMedium),
      titlePadding: theme.spacing(0.5),
      columnPadding: theme.spacing(1.5),
      rowPadding: theme.spacing(0.5),
      padding: theme.spacing(1),
      symbolSize: theme.spacing(1.5) ** 2,
    },
    range: {
      category: categoricalColors,
      ordinal: categoricalColors,
    },
    mark: {
      color: primaryChartColor,
    },
    bar: {
      color: primaryChartColor,
      cornerRadiusEnd: theme.shape.borderRadius,
      discreteBandSize: { band: 0.75 },
    },
    line: {
      color: primaryChartColor,
      strokeCap: 'round',
      strokeWidth: markStrokeWidth,
    },
    area: {
      color: primaryChartColor,
      opacity: 0.18,
    },
    point: {
      color: primaryChartColor,
      filled: true,
      size: 40,
    },
    circle: {
      color: primaryChartColor,
      filled: true,
      size: 40,
    },
    rule: {
      color: primaryChartColor,
      strokeWidth: markStrokeWidth,
    },
    text: {
      color: textSubdued,
      font: bodyFontFamily,
      fontSize: bodyFontSize,
    },
    view: {
      continuousHeight: theme.spacing(35),
      discreteHeight: { step: theme.spacing(4.5) },
      stroke: 'transparent',
    },
  };
};
